import { useRef, useEffect, useState } from 'react';
import cn from 'classnames';

import { backgrounds } from 'js/utils/np-colors';

import Icon, { type IconName } from 'components/icon/Icon';

import { HoverData } from '../steps-menu/StepsMenu';

import { IStep } from 'source/NFR.types';

interface StepProps extends IStep {
  index: number;
  style?: React.CSSProperties;
  setOnHoverData: (data: HoverData) => void;
  isVerticallyStyled: boolean;
  activateStep: (index: number) => void;
}

const Step: React.FC<StepProps> = ({
  text,
  index,
  style,
  setOnHoverData,
  isVerticallyStyled,
  activateStep,
  isActive
}) => {
  const stepTextRef = useRef<HTMLSpanElement>(null);
  const [textWidth, setTextWidth] = useState<number>(0);
  const [isHovering, setIsHovering] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (!stepTextRef.current) return;
    const { clientWidth } = stepTextRef.current;
    setTextWidth(clientWidth);
  }, [isVerticallyStyled]);

  const onHover = (mouseEntered: boolean, e?: React.MouseEvent) => {
    if (e) {
      setOnHoverData({
        mousePos: [e.clientX, e.clientY],
        hoverAnimation: mouseEntered
      });
    }
    setIsHovering(mouseEntered);
  };

  return (
    <button
      className={cn('step', {
        'step--hover-off': isHovering === false,
        'step--hover-on': isHovering,
        'step--is-big': !isVerticallyStyled,
        'step--is-active': isActive,
        'step--light-text': index === 3
      })}
      style={style}
      onMouseEnter={e => onHover(true, e)}
      onMouseLeave={e => onHover(false, e)}
      onClick={() => activateStep(index)}
    >
      <div
        className="step-circle"
        style={{
          width: isActive || (isHovering && isVerticallyStyled) ? `calc(${textWidth}px + 4rem)` : undefined,
          ...backgrounds(index)
        }}
      >
        <Icon className="step-number" name={`np-${index + 1}` as IconName} fill />
        <span className="step-text" ref={stepTextRef}>
          {text}
        </span>
      </div>
    </button>
  );
};

Step.displayName = 'Step';

export default Step;
